import { debounce, formatCentsInMoneyTemplate } from '@/entrypoints/utils';
import { Cart, CartEvents, TypedCartEvent } from '../internals/core';
import { CartEventBus } from '../internals/event-bus';
import { MiniCart } from './mini-cart';
import { createLogger } from '@/lib/logger';

class MiniCartSummary extends HTMLElement {
  private abortController: AbortController | null = null;
  private eventBus: CartEventBus | null = null;
  private logger = createLogger('mini-cart-summary');
  private initialPriceElem: HTMLElement | null = null;
  private discountsElem: HTMLElement | null = null;
  private finalPriceElem: HTMLElement | null = null;
  private priceFormatTemplate: string | undefined;

  constructor() {
    super();
  }

  // Add internal logic here as separated functions
  private onCartChanged(e: TypedCartEvent<'cart:changed:debounced'>) {
    const { cart } = e.detail;
    this.render(cart);
  }

  private debouncedOnCartChange = debounce(this.onCartChanged.bind(this), 200);

  private render(cart: Cart) {
    this.classList.toggle('hidden', cart.total_price === 0);

    if (!this.priceFormatTemplate) return;

    if (this.initialPriceElem) {
      this.initialPriceElem.innerHTML = formatCentsInMoneyTemplate(
        this.priceFormatTemplate,
        cart.original_total_price,
      );
    }

    if (this.discountsElem) {
      this.discountsElem.innerHTML = `-${formatCentsInMoneyTemplate(this.priceFormatTemplate, cart.total_discount)}`;
    }

    if (this.finalPriceElem) {
      this.finalPriceElem.innerHTML = formatCentsInMoneyTemplate(
        this.priceFormatTemplate,
        cart.total_price,
      );
    }
  }

  private bindEvents() {
    this.eventBus!.on(CartEvents.DebouncedChange, this.debouncedOnCartChange);
  }

  private unbindEvents() {
    this.eventBus!.off(CartEvents.DebouncedChange, this.debouncedOnCartChange);
  }

  private onMiniCartReady(parentMiniCart: MiniCart) {
    // Make sure we use the same event bus as the parent mini-cart.
    this.eventBus = parentMiniCart.bus;

    this.bindEvents();
  }

  // Lifecycle hooks
  connectedCallback() {
    this.abortController = new AbortController();

    const parentMiniCart = this.closest('mini-cart')! as MiniCart;
    if (!parentMiniCart) {
      this.logger.error('MiniCartHeader must be a child of a MiniCart element');
      throw new Error('MiniCartHeader must be a child of a MiniCart element');
    }

    parentMiniCart.execOnReady(this.onMiniCartReady.bind(this, parentMiniCart));

    this.priceFormatTemplate = this.getAttribute('data-price-format') ?? undefined;

    this.initialPriceElem = this.querySelector('[data-cart-subtotal] .price') as HTMLElement | null;
    this.discountsElem = this.querySelector('[data-cart-discounts] .price') as HTMLElement | null;
    this.finalPriceElem = this.querySelector(
      '[data-cart-final-price] .price',
    ) as HTMLElement | null;
  }

  disconnectedCallback() {
    this.abortController?.abort();

    this.unbindEvents();
  }
}

if (!customElements.get('mini-cart-summary')) {
  customElements.define('mini-cart-summary', MiniCartSummary);
}
